/**
 * The LLM report-generation seam on the front end (TV1-1938 · S23 / ADR-051).
 *
 * `POST /analytics/generate-report` returns an UNPERSISTED, layout-shaped preview:
 * structurally a `UiWidgetsLayout` **minus `id`**, with **positionless** widgets.
 * This module turns that wire body into something the existing report engine can
 * render, and turns the endpoint's 422 into something a seller can act on.
 *
 * ## Why there is no new command, aggregate or readmodel
 *
 * The preview is layout-shaped precisely so that **"Save" is a pure pass-through**:
 * mint a uuid, hand the body to the `AddUiWidgetLayout` the manual builder already
 * drives. A generated report is then a first-class editable layout, identical to a
 * hand-built one — no second class of report, and nothing to migrate later.
 *
 * Generating and saving stay separate user intents: a report the seller never asked
 * to keep must not appear in their report tree. So nothing here writes.
 *
 * ## Why the widgets have no coordinates
 *
 * `UiWidgetsLayoutSchema` has had `x`/`y`/`width`/`height` optional since TV1-1931,
 * explicitly so an LLM would not have to solve 2D bin-packing. `autoGrid.resolveLayout`
 * (called from a `useMemo` inside `WidgetLayoutComponent`) places them row-major over
 * DECLARATION ORDER at render time.
 *
 * ⚠️ **Widget order is therefore the reading order**, and it must survive this module
 * untouched. Reordering here — sorting, grouping, "KPIs first" — would silently
 * rewrite the report the model composed.
 */
import type { PersistedWidgetLayout } from '@components/application/WidgetLayout/types';
import type { DomainWidgetDefinition } from '@components/application/WidgetLayout/Widgets/widgetDefinitionAdapter';

/** The 200 body of `POST /analytics/generate-report`. */
export type GeneratedReportBody = {
  layout: {
    name: string;
    description?: string;
    type: 'layout';
    /** In READING ORDER, and POSITIONLESS. */
    widgets: Array<{ definition: DomainWidgetDefinition }>;
  };
};

/**
 * Why a generation attempt produced no report.
 *
 * `unanswerable` is the model saying, through a channel reserved in its own output
 * schema, that the Cube catalog cannot support the request — as opposed to it simply
 * failing to follow the grammar. The distinction matters to the seller: one is *"try
 * rephrasing"*, the other is *"we don't have that data"*, and conflating them would
 * send people round a loop rephrasing a question we can never answer.
 *
 * **TV1-1939 (S24) consumes this**: it adds a "nearest valid report" alongside the
 * explanation, and a Grafana metric so schema gaps become a product feedback loop.
 * The reason is already on the wire (`details.reason`), so that ticket adds a branch
 * here rather than reshaping the contract.
 */
export type GenerationFailureReason = 'validation-exhausted' | 'unanswerable' | 'unknown';

export type GenerationFailure = {
  reason: GenerationFailureReason;
  /** The model's own words. Only ever present for `unanswerable`. */
  explanation?: string;
  /** Catalog concepts the request needed and the catalog lacks. */
  missingConcepts?: string[];
  /**
   * The NEAREST VALID REPORT (TV1-1939 · S24) — the closest report the catalog CAN
   * support, when the model had something honest to offer. Only ever present for
   * `unanswerable`, and **frequently absent**: the server would rather send nothing
   * than a report that quietly answers a question the seller did not ask.
   *
   * ⚠️ It rides inside the **422**, not a 200. The seller sees it BEHIND the
   * explanation banner — never instead of it. Rendering this without the banner would
   * turn an honest "we can't answer that" into a confidently-irrelevant report, which
   * is the exact failure the whole feature exists to prevent.
   */
  nearestReport?: GeneratedReportBody;
};

/** The `pick(['status','type','message','details','code'])` body PV3's express adapter sends. */
type ErrorBody = {
  code?: string;
  details?: {
    reason?: string;
    explanation?: string;
    missingConcepts?: string[];
    nearestReport?: GeneratedReportBody;
  };
};

/**
 * The client library REJECTS a non-2xx with the `Response` augmented with a parsed
 * `bodyParsed` (`processFetchResponse` in `teselly-client-library/base/SSE.ts`) — it
 * does NOT throw an `Error`. Reading `err.message` here would yield `undefined` and
 * we would report every failure as "unknown", including the ones we went to real
 * trouble to distinguish.
 */
type RejectedResponse = { status?: number; bodyParsed?: ErrorBody };

const isRecord = ( value: unknown ): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

/**
 * A nearest report worth SHOWING — the client-side half of "never an empty widget".
 *
 * The server already guarantees this (`assembleReport` enforces ≥1 widget and ≥1 data
 * widget before anything becomes a `nearestReport`). Re-checking it here is not
 * belt-and-braces paranoia: this value arrives as untyped JSON out of an ERROR body,
 * which no response schema validates. A `{ widgets: [] }` that slipped through would
 * render as a titled, empty grid sitting under a banner that says we could not answer
 * — which reads as "the system is broken", not "we don't have that data".
 *
 * The banner is the honest answer on its own. The preview is a bonus or it is nothing.
 */
const hasNearestReport = ( report: unknown ): report is GeneratedReportBody => {
  if ( !isRecord( report )) return false;
  const layout = report.layout;
  return isRecord( layout )
    && Array.isArray( layout.widgets )
    && layout.widgets.length > 0;
};

/**
 * Classify a failed generation.
 *
 * Anything we do not recognise degrades to `unknown` rather than being presented as
 * an `unanswerable` — claiming "your catalog can't answer that" when the truth is
 * "the network blipped" is a confidently wrong explanation, and the seller would
 * stop asking for something they could in fact have.
 */
export const toGenerationFailure = ( error: unknown ): GenerationFailure => {
  if ( !isRecord( error )) return { reason: 'unknown' };

  const { bodyParsed } = error as RejectedResponse;
  if ( !bodyParsed || bodyParsed.code !== 'REPORT_GENERATION_FAILED' ) {
    return { reason: 'unknown' };
  }

  const details = bodyParsed.details;

  if ( details?.reason === 'unanswerable' ) {
    return {
      reason: 'unanswerable',
      explanation: details.explanation,
      missingConcepts: details.missingConcepts ?? [],
      // Absent unless the server actually built one. `hasNearestReport` below is the
      // only thing that should decide whether a preview appears — never a truthiness
      // check on an empty layout, which is how an "empty widget" would sneak in.
      ...( hasNearestReport( details.nearestReport ) ? { nearestReport: details.nearestReport } : {})
    };
  }

  if ( details?.reason === 'validation-exhausted' ) {
    return { reason: 'validation-exhausted' };
  }

  return { reason: 'unknown' };
};

/**
 * The generated report -> a layout the existing engine renders and can save.
 *
 * `id` is minted HERE, on the client, and it is the SAME id the eventual
 * `AddUiWidgetLayout` uses — so previewing and saving cannot disagree about which
 * layout this is, and saving twice cannot create two.
 *
 * The widget envelopes stay POSITIONLESS on purpose: `resolveLayout` places them, and
 * the first human drag persists the resolved coordinates through the existing
 * `ChangeUiWidgetLayout` path. Materialising coordinates here would freeze a layout
 * the seller never arranged.
 */
export const toPreviewLayout = (
  body: GeneratedReportBody,
  mintId: () => string = () => crypto.randomUUID()
): PersistedWidgetLayout => {
  const { layout } = body;

  return {
    id: mintId(),
    type: 'layout',
    name: layout.name,
    ...( layout.description ? { description: layout.description } : {}),
    // Order preserved EXACTLY — it is the reading order. No sort, no group, no filter.
    widgets: layout.widgets.map(({ definition }) => ({ definition }))
  } as PersistedWidgetLayout;
};
